import type { FC } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';

import { FollowUpButton } from '@atlassian/conversation-assistant-ui-components';
import type { FollowUpObject } from '@atlassian/ai-agent-streaming';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

export type PageCatchupFollowUpComponentProps = {
	followUpContent: FollowUpObject[];
	pageCatchupContent: string;
	contentPath: string;
	setIsOpen: (open: boolean) => void;
};

const i18n = defineMessages({
	pageCatchupUserQuery: {
		id: 'page-catchup.follow-up.initial-human-prompt',
		defaultMessage:
			'Summarize the changes that have happened on this content since my last visit {link}',
		description:
			'When user decides to continue conversation after summarizing the changes that have happened on the page, this message will be shown in the chatbox as the original prompt that user sent',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const FollowUpButtonWrapper = styled.div({
	// This is necessary due to a bug (DSP-19088) with the Split Button,
	// but we won't need it for long as the button will soon be replaced.

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		borderEndEndRadius: `${token('radius.xsmall')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		borderStartEndRadius: `${token('radius.large')} !important`,
		width: '100%',
	},
});

/**
 * Sanitizes HTML content by removing HTML tags while preserving text content and structure
 * @param htmlContent - The HTML content to sanitize
 * @returns The sanitized text content without HTML tags
 */
const sanitizeHtmlContent = (htmlContent: string): string => {
	// First, let's process the HTML to ensure proper structure
	let processedHtml = htmlContent
		// Replace heading tags with their text content followed by double newlines to create section breaks
		.replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n\n$1\n')
		// Ensure bullet points start with proper formatting
		.replace(/\* /g, '\n* ')
		// Handle bullet points that might be at the start of lines
		.replace(/^\s*\*\s/gm, '\n* ');

	// Create a temporary div to extract text content
	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = processedHtml;

	let textContent = tempDiv.textContent || tempDiv.innerText || '';

	// Clean up the text content
	textContent = textContent
		.replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple line breaks with double line breaks for section separation
		.replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space (but preserve line breaks)
		.trim(); // Remove leading/trailing whitespace

	// Remove all instances of [number] since we don't have inline linking in chat yet
	textContent = textContent.replace(/ ?\[[0-9]+\]/g, '');

	return textContent;
};

export const PageCatchupFollowUpComponent: FC<PageCatchupFollowUpComponentProps> = ({
	followUpContent,
	pageCatchupContent,
	contentPath,
	setIsOpen,
}) => {
	const publish = usePublish('ai-mate');
	const { formatMessage } = useIntl();

	const { trackAIResultAction } = useAIEventsInstrumentation();

	const contentLink = `${window.location.origin}${contentPath}`;
	const sanitizedContent = sanitizeHtmlContent(pageCatchupContent);

	return (
		<Stack space="space.100">
			{followUpContent.map((followUp, index) => (
				<FollowUpButtonWrapper key={index}>
					<FollowUpButton
						key={followUp.text}
						followUpIndex={index}
						followUpText={followUp.text}
						followUpType={followUp.type}
						experienceId="page-catchup"
						onClick={() => {
							trackAIResultAction('followUpQuestionClick');
							publish({
								type: 'chat-new',
								source: 'page-catchup',
								data: {
									name: followUp.text.substring(0, 30),
									dialogues: [
										{
											human_message: {
												content: formatMessage(i18n.pageCatchupUserQuery, {
													link: contentLink,
												}),
											},
											agent_message: {
												content: sanitizedContent,
											},
										},
									],
									prompt: followUp.text,
								},
							});

							setIsOpen(false);
						}}
					/>
				</FollowUpButtonWrapper>
			))}
		</Stack>
	);
};
